/**
 * 36協定の上限状況。専用画面とダッシュボードのカードが、同じ言葉で話すための場所。
 *
 * <h3>なぜ1か所にまとめるのか</h3>
 *
 * 区分の名前を画面ごとに書くと、片方だけ直したときに黙ってずれる。
 * いちばん困るずれ方は<b>「判定できなかった」が「問題なし」と同じ見た目に
 * なること</b>で、そうなると見ていないことが見えなくなる。だから
 * 名前も、既定の対象月も、送る条件も、ここだけで決める。
 */

/** サーバが返す区分。強い順。 */
export const OVERTIME_LIMIT_LEVELS = [
  'violation',
  'warning',
  'obligation',
  'unknown'
]

/**
 * 覚えのない区分は「判定できなかった」に寄せる。
 *
 * <b>「触れていない」へ寄せない。</b>知らない値が来たとき、それは
 * 上限に触れていないことの証拠ではない。
 */
export function overtimeLimitLevelKey (level) {
  const text = String(level || '')
  return OVERTIME_LIMIT_LEVELS.indexOf(text) >= 0 ? text : 'unknown'
}

/** 月次集計の印（src/admin/total/List.vue）と同じ4区分にそろえる。 */
export function overtimeLimitLevelLabel (level) {
  switch (overtimeLimitLevelKey(level)) {
    case 'violation':
      return '超過'
    case 'warning':
      return '注意'
    case 'obligation':
      return '要通知'
    default:
      return '不明'
  }
}

/**
 * 上限のお知らせが届かない理由。サーバが返す名前をそのまま使う。
 *
 * <b>画面ごとに言葉を作らない。</b>同じ状態を自社設定の画面でも、
 * 幹部が見る専用画面でも出すので、片方だけ直すと黙ってずれる。
 */
export const OVERTIME_ALERT_DELIVERY_BLOCKERS = [
  'SLACK_UNAVAILABLE',
  'TURNED_OFF',
  'NO_DESTINATION',
  'NO_DUE_DATE'
]

/**
 * いま画面に入っている設定なら届くか。**編集中の画面のためだけにある。**
 *
 * <b>決めているのはサーバ。</b>保存すればサーバが同じ判定を返す。それでも
 * ここに置くのは、編集の途中で「いま通知先を消したら届かなくなる」ことを
 * その場で見せたいから。保存してから知らせても、直すのに1往復かかる。
 *
 * 並びはサーバと同じにする（契約が無い → 切っている → 送り先が無い →
 * 期日が無い）。ずれると、同じ設定で画面ごとに違う理由が出る。
 * その並びは scripts/check_overtime_alert_blocker_parity.js が突き合わせる。
 *
 * **契約をいちばん先に見る。**契約が無い会社のフラグは、サーバが表示のために
 * 落として返す。だからこの画面の値はいつも「切」で、順番を後ろにすると
 * サーバは「送り先が無い」、この画面は「切っている」と別のことを言う。
 *
 * @param {object} form 自社設定の画面が持っている値
 * @returns {string} 'NONE' / 'SLACK_UNAVAILABLE' / 'TURNED_OFF' /
 *                   'NO_DESTINATION' / 'NO_DUE_DATE'
 */
export function overtimeAlertBlockerOf (form) {
  const source = form || {}
  const filled = value => String(value == null ? '' : value).trim().length > 0

  if (!source.slack_feature_enabled) return 'SLACK_UNAVAILABLE'
  if (!source.overtime_alert_notification_flg) return 'TURNED_OFF'

  const tokenReady = !source.clear_slack_token &&
    (Boolean(source.slack_token_configured) || filled(source.slack_token))
  /*
   * 管理部門向けのチャンネルへ倒せるのは、**その朝通知が「入」のときだけ。**
   * オフにしてもチャンネルIDの値は消えない（入力が disabled になるだけ）
   * ので、やめた会社の欄には文字列が残る。残っている＝送ってよい、では
   * ないことをサーバ側と同じに扱う。
   */
  const sharedChannel = Boolean(source.management_daily_notification_flg) &&
    filled(source.management_daily_slack_channel)
  const anyChannel = filled(source.overtime_alert_slack_channel) || sharedChannel
  if (!tokenReady || !anyChannel) return 'NO_DESTINATION'

  if (!filled(source.overtime_calculation_due_kind) ||
      !filled(source.overtime_calculation_due_day)) {
    return 'NO_DUE_DATE'
  }
  return 'NONE'
}

/** 専用の通知先が空で、管理部門向けのチャンネルへ届くか。 */
export function overtimeAlertUsesManagementChannelOf (form) {
  const source = form || {}
  return overtimeAlertBlockerOf(source) === 'NONE' &&
    String(source.overtime_alert_slack_channel || '').trim().length === 0
}

/**
 * 届かないことを、利用者の言葉で伝える。
 *
 * <h3>なぜ画面に出すのか</h3>
 *
 * このお知らせは、会社が何も設定しなくても届く側で立ち上がる。それでも
 * 通知先を1つも決めていない会社には届かないし、算定期日を消した会社にも
 * 届かない。**そのとき、エラーは1行も出ない。**毎月ただ何も起きないので、
 * 気づけるのは「上限を超えたのに誰にも届かなかった」あとになる。
 *
 * だから**何をすれば直るか**まで書く。状態だけ出しても直せない。
 *
 * @param {string} blocker サーバが返す理由。届くなら 'NONE'
 * @param {boolean} usesManagementChannel 管理部門向けのチャンネルへ届くか
 * @param {string} [lastFailedMonth] 最後に送れなかった対象月（`YYYY-MM`）。
 *        設定はそろっているのに送信そのものが失敗したときだけ入る
 * @returns {{tone: string, headline: string, detail: string}|null}
 *          出すものが無ければ null
 */
export function overtimeAlertDeliveryNotice (
  blocker, usesManagementChannel, lastFailedMonth) {
  switch (String(blocker || '')) {
    case 'SLACK_UNAVAILABLE':
      /*
       * **ここだけは何も出さない。**
       *
       * ほかの理由は「こうすれば直る」と書ける。契約にSlack通知が無い
       * 会社には、書ける直し方が画面の中に1つも無い——通知の欄そのものが
       * 操作できない状態で出ている。そこへ「届いていません」を足すと、
       * **直せない警告が幹部の画面に居座るだけ**になる。
       *
       * 黙るのは「分からないから」ではない。分かったうえで、この会社は
       * Slackの通知を頼んでいない、と読む。上限の判定そのものは専用画面と
       * ダッシュボードに出ていて、そちらは契約に関係なく動く。
       */
      return null
    case 'NO_DESTINATION':
      return {
        tone: 'warning',
        headline: '上限のお知らせが届いていません',
        detail: '通知先が設定されていません。自社設定のSlack通知で、' +
          'Slackの接続と通知先を登録すると、算定した月に1回だけ' +
          'お知らせします。'
      }
    case 'NO_DUE_DATE':
      return {
        tone: 'warning',
        headline: '上限のお知らせが届いていません',
        detail: '算定期日が設定されていません。自社設定で算定期日を選ぶと、' +
          'その日に月1回だけお知らせします。'
      }
    case 'TURNED_OFF':
      return {
        tone: 'warning',
        headline: '上限のお知らせを停止しています',
        detail: '自社設定でオンにすると、算定した月に1回だけお知らせします。' +
          'お知らせに氏名や部署名は含めません。'
      }
    case 'NONE':
      /*
       * 設定はそろっている。**それでも送れていないことがある。**
       * 通知先チャンネルに通知用のアプリが入っていない、チャンネルIDの
       * 打ち間違い。設定を見ているだけでは絶対に分からないし、
       * 気づかなければ次の機会は翌月になる。
       */
      if (lastFailedMonth) {
        return {
          tone: 'warning',
          headline: '上限のお知らせを送れませんでした',
          detail: overtimeLimitMonthLabel(lastFailedMonth) +
            'ぶんのお知らせが送れていません。通知先のチャンネルに' +
            '通知用のアプリが参加しているか、チャンネルIDが正しいかを' +
            'ご確認ください。'
        }
      }
      /*
       * 届くときは黙る——ただし**どこへ届くかが変わるときだけは言う。**
       * 専用の通知先を決めていない会社には、管理部門向けと同じ場所へ
       * 届く。扱いは軽い（氏名も部署名も入らない）が、出ている場所は
       * 会社が知っているべき。
       */
      if (usesManagementChannel) {
        return {
          tone: 'info',
          headline: '上限のお知らせは管理部門向けのチャンネルに届きます',
          detail: '専用の通知先を設定していないため、管理部門向けの通知と' +
            '同じ場所へお送りします。お知らせに氏名や部署名は含めません。'
        }
      }
      return null
    default:
      /*
       * **覚えのない値を「届いている」に読み替えない。**
       *
       * 空も、知らない名前もここへ来る。黙ると、いちばん出したい状態だけが
       * 画面から消えて、届いている会社と1ドットも変わらなくなる。
       * 区分の寄せ方（overtimeLimitLevelKey）と同じで、分からないものは
       * 分からないと出す。
       */
      return {
        tone: 'warning',
        headline: '上限のお知らせが届いているか確認できません',
        detail: 'お知らせの状態を取得できませんでした。画面を開き直しても' +
          '変わらない場合は、自社設定のSlack通知をご確認ください。'
      }
  }
}

/** `YYYY-MM` を「◯年◯月」に。読めなければそのまま返す。 */
function overtimeLimitMonthLabel (targetMonth) {
  const matched = String(targetMonth || '').match(/^(\d{4})-(\d{1,2})$/)
  return matched
    ? matched[1] + '年' + Number(matched[2]) + '月'
    : String(targetMonth || '')
}

/**
 * 既定の対象月。**当月ではなく前月。**
 *
 * 当月はまだ勤務表が埋まっていないので、開いた瞬間に
 * 「判定できなかった」ばかりが並ぶ。会社が定めた算定期日で送る
 * 通知も、期日が属する月の前月を対象にしている。画面と通知で
 * 違う月を見ていると、届いた人数と画面の人数が合わない。
 *
 * @param {Date} [now] 判定の基準時刻。省略すると今。
 * @returns {string} `YYYY-MM`
 */
export function defaultOvertimeLimitMonth (now) {
  const base = now instanceof Date ? new Date(now.getTime()) : new Date()
  const year = base.getFullYear()
  const month = base.getMonth() /* 0始まりなので、この時点で既に前月 */
  const previous = new Date(year, month, 1)
  previous.setMonth(previous.getMonth() - 1)
  return previous.getFullYear() + '-' +
    String(previous.getMonth() + 1).padStart(2, '0')
}

/**
 * サーバへ送る条件。**一覧と件数の両方へ、これを渡す。**
 *
 * 片方だけに送ると「件数はあるのに一覧が空」あるいはその逆になり、
 * ページ送りが壊れる。条件を作る場所を1つにして、分かれないようにする。
 *
 * @param {string} targetMonth `YYYY-MM`
 * @returns {{year: string, month: string}|null} 読めない月なら null
 */
export function overtimeLimitConditionParams (targetMonth) {
  const matched = String(targetMonth || '').match(/^(\d{4})-(\d{2})$/)
  if (!matched) return null

  const month = Number(matched[2])
  if (month < 1 || month > 12) return null

  return { year: matched[1], month: String(month) }
}
